import React, { useState } from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';

import { Button } from '@flywire/headlessui-tailwindcss';

import { userExists } from 'actions';
import type { FieldValueType } from 'components/FieldList/Field/Field';
import { Popover } from 'components/Popover/Popover';
import { UserAccessModal } from 'components/UserAccessModal/UserAccessModal';
import { UserLoginOrigin } from 'contexts/LoginContext/LoginContext';
import type { SanitizedField } from 'models';
import type { RootState } from 'reducers/types';
import { isEmbedded, isLoggedIn, isUserCreated } from 'selectors';
import { useTranslations } from 'utils';

import { TextInputField } from '../../FieldList/Field/TextInputField/TextInputField';

import './CAPSenderEmailInputField.scss';

interface CAPSenderEmailInputFieldType
  extends SanitizedField<Array<FieldValueType>, string> {}

type CAPSenderEmailInputFieldProps = ConnectedProps<typeof connector> & {
  field: CAPSenderEmailInputFieldType;
  handleChange: (name: string, value: string) => void;
  handleBlur: (name: string) => boolean;
};

function CAPSenderEmailInputFieldComponent({
  field,
  handleChange,
  handleBlur,
  userExists,
  isEmbedded,
  isLoggedIn,
  isUserCreated,
}: CAPSenderEmailInputFieldProps) {
  const [isPopoverOpen, setPopoverOpen] = useState(true);
  const [isValidEmail, setIsValidEmail] = useState(false);
  const closePopover = () => setPopoverOpen(false);
  const openPopover = () => setPopoverOpen(true);

  const validateEmail = async (name: string) => {
    const isValid = await handleBlur(name);
    if (isValid) {
      await userExists();
    }
    setIsValidEmail(isValid);
  };

  const changeEmail = (name: string, value: string) => {
    setIsValidEmail(false);
    openPopover();
    handleChange(name, value);
  };
  const shouldShowPopover = isValidEmail && !isEmbedded && !isLoggedIn;

  return (
    <aside
      data-testid="cashback-sender-email-input-field"
      className="CAPSenderEmailInputField"
    >
      <TextInputField
        handleBlur={validateEmail}
        handleChange={changeEmail}
        // @ts-expect-error check field value type
        field={field}
      />
      {shouldShowPopover && (
        <PopoverContent
          handleClose={closePopover}
          isOpen={isPopoverOpen}
          isUserCreated={isUserCreated}
        />
      )}
    </aside>
  );
}

function PopoverContent({
  handleClose,
  isOpen,
  isUserCreated,
}: {
  handleClose: () => void;
  isOpen: boolean;
  isUserCreated: boolean;
}) {
  const [showUserAccessModal, setShowUserAccessModal] = useState(false);
  const [showSignupTab, setShowSignupTab] = useState(false);
  const openModalLogin = () => {
    setShowSignupTab(false);
    setShowUserAccessModal(true);
  };

  const openModalSignup = () => {
    setShowSignupTab(true);
    setShowUserAccessModal(true);
  };

  const closeUserAccessModal = () => setShowUserAccessModal(false);

  const handleLoginSuccess = async () => {
    closeUserAccessModal();
  };

  const i18n = useTranslations();
  const titleDefault = isUserCreated
    ? (i18n.t('senderInformation.login.title') as string)
    : (i18n.t('senderInformation.createUser.title') as string);
  const subtitleDefault = isUserCreated
    ? (i18n.t('senderInformation.login.subtitle') as string)
    : (i18n.t('senderInformation.createUser.subtitle') as string);
  const actionButton = isUserCreated
    ? (i18n.t('userAccessModal.login') as string)
    : (i18n.t('userAccessModal.signup') as string);
  return (
    <>
      <Popover.Wrapper isOpen={isOpen}>
        <Popover.CloseButton handleClose={handleClose} />
        <Popover.Content className="PopoverContent">
          <div className="PopoverContent-loginSignup">
            <Popover.Title text={titleDefault} />
            <Popover.Subtitle text={subtitleDefault} />
            <Button
              tone="primary"
              className="w-full md:w-60"
              onClick={isUserCreated ? openModalLogin : openModalSignup}
              data-testid="cashback-sender-email-input-button-login-signup"
              aria-label={'login-signup-button'}
            >
              {i18n.t(actionButton)}
            </Button>
          </div>
        </Popover.Content>
      </Popover.Wrapper>

      <UserAccessModal
        isOpen={showUserAccessModal}
        showSignupTab={showSignupTab}
        onClose={closeUserAccessModal}
        onLoginSuccess={handleLoginSuccess}
        onSignUpSuccess={handleLoginSuccess}
        loginOrigin={UserLoginOrigin.PROMPT}
      />
    </>
  );
}

const mapStateToProps = (state: RootState) => ({
  isEmbedded: isEmbedded(state),
  isLoggedIn: isLoggedIn(state),
  isUserCreated: isUserCreated(state),
});

const mapDispatchToProps = {
  userExists,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
const CAPSenderEmailInputField = connector(CAPSenderEmailInputFieldComponent);

export { CAPSenderEmailInputField };
